/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Ring } from '@uiball/loaders';
import { CustomSelect } from '../../../shared/components/CustomSelect';
import {
  getAuctionList,
  addAuction,
  getCategory,
} from '../../../utils/services/user.service';
import Pagination from '../../../shared/components/Pagination';
import AuctionCard from './components/AuctionCard';
import useCountdown from './components/CountdownTimer';
import {
  PageContainer,
  Header,
  Title,
  SectionTitle,
  Grid,
  PrimaryButton,
  LoadingOverlay,
  EmptyState,
} from './components/shared';

const CreateAuctionButton = styled(PrimaryButton)`
  font-size: 0.875rem;
  padding: 0.75rem 1.5rem;
`;

// Sidebar styled components
const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;


const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 450px;
  max-width: 90vw;
  height: 100vh;
  background: #0B1122;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  z-index: 1001;
  overflow-y: auto;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(450px)'};
  transition: transform 0.3s ease;
`;

const SidebarTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &[type="datetime-local"] {
    &::-webkit-calendar-picker-indicator {
      filter: invert(1);
      cursor: pointer;
      opacity: 1;
    }
    
    &::-webkit-datetime-edit {
      color: white;
    }
    
    &::-webkit-datetime-edit-fields-wrapper {
      color: white;
    }
    
    &::-webkit-datetime-edit-text {
      color: white;
    }
    
    &::-webkit-datetime-edit-month-field,
    &::-webkit-datetime-edit-day-field,
    &::-webkit-datetime-edit-year-field,
    &::-webkit-datetime-edit-hour-field,
    &::-webkit-datetime-edit-minute-field {
      color: white;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SelectStyled = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  option {
    background: #0B1122;
    color: white;
  }
`;

const FileUploadButton = styled.label`
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: white;
  cursor: pointer;
  text-align: center;
  display: block;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const SelectWrapper = styled.div`
  width: 100%;
  
  /* Target react-select control */
  .selectDropdpenReact > div {
    background-color: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    min-height: auto;
    height: auto;
    
    &:hover {
      border-color: #5ce9ff !important;
      background-color: rgba(255, 255, 255, 0.05) !important;
    }
  }
  
  /* Target all possible react-select control classes */
  [class*="control"] {
    background-color: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    
    &:hover {
      border-color: #5ce9ff !important;
      background-color: rgba(255, 255, 255, 0.05) !important;
    }
  }
  
  /* Target single value */
  [class*="singleValue"] {
    color: white !important;
  }
  
  /* Target placeholder */
  [class*="placeholder"] {
    color: #9ca3af !important;
  }
  
  /* Target indicators */
  [class*="indicator"] {
    color: white !important;
    svg {
      fill: white !important;
    }
  }
`;

const TierTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  
  th, td {
    padding: 0.5rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  th {
    font-weight: 600;
    color: #9ca3af;
  }
  
  button {
    background: #ff3b6b;
    border: none;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.7rem;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const TierInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: stretch;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  input {
    flex: 1;
    min-width: 0;
  }
`;

const AddTierButton = styled.button`
  padding: 0.75rem 1rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: opacity 0.2s;
  height: 100%;
  align-self: stretch;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeatureInputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  
  input {
    flex: 1;
  }
  
  button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(to right, #00f7d2, #5ce9ff);
    border: none;
    color: black;
    cursor: pointer;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const AuctionList = () => {
  const history = useHistory();
  const [auctions, setAuctions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(50);
  const [countdowns, setCountdowns] = useState({});
  const [isComingSoon, setIsComingSoon] = useState({});
  
  // Auction sidebar state
  const [auctionSidebarOpen, setAuctionSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isCreatingAuction, setIsCreatingAuction] = useState(false);
  
  // Auction form state
  const [auctionTitle, setAuctionTitle] = useState('');
  const [auctionBrand, setAuctionBrand] = useState('');
  const [auctionDescription, setAuctionDescription] = useState('');
  const [auctionStartTime, setAuctionStartTime] = useState('');
  const [auctionEndTime, setAuctionEndTime] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [productCondition, setProductCondition] = useState('New');
  const [pickupAddress, setPickupAddress] = useState('');
  const [warrantyMonths, setWarrantyMonths] = useState(0);
  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [tierMinQuantity, setTierMinQuantity] = useState('');
  const [tierMaxQuantity, setTierMaxQuantity] = useState('');
  const [tierPrice, setTierPrice] = useState('');
  const [features, setFeatures] = useState([{ id: 1, value: '' }]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.data?.user?.role || 'viewer';
  const isWholeseller = userRole === 'wholesaler';

  // Get coin list from Redux
  const { coinList1 } = useSelector((state) => ({
    coinList1: state.coinList1.data || [],
  }));

  useEffect(() => {
    fetchAuctions(1);
    fetchCategories();
  }, []);

  // Real-time countdown for auctions
  useEffect(() => {
    if (auctions.length === 0) return;

    const updateCountdowns = () => {
      const newCountdowns = {};
      const newIsComingSoon = {};
      // Use UTC time for consistent comparisons (getTime() returns UTC milliseconds)
      const nowUTC = Date.now();
      
      auctions.forEach((auction) => {
        // Check if auction has started (using UTC)
        const startTime = auction.auction_start_time || auction.startDate;
        if (startTime) {
          const startDate = new Date(startTime);
          // Ensure we're comparing UTC times
          const startUTC = startDate.getTime();
          if (startUTC > nowUTC) {
            // Auction hasn't started yet
            newIsComingSoon[auction._id] = true;
            newCountdowns[auction._id] = null;
            return;
          }
        }
        
        newIsComingSoon[auction._id] = false;
        
        const endTime = auction.acution_end_time || auction.auction_end_time;
        if (!endTime) {
          newCountdowns[auction._id] = 'Ongoing';
          return;
        }

        const endDate = new Date(endTime);
        // Compare UTC times (getTime() returns UTC milliseconds)
        const diff = endDate.getTime() - nowUTC;

        if (diff <= 0) {
          newCountdowns[auction._id] = 'ended';
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          
          if (hours > 0) {
            newCountdowns[auction._id] = `${hours}h ${minutes}m ${seconds}s`;
          } else if (minutes > 0) {
            newCountdowns[auction._id] = `${minutes}m ${seconds}s`;
          } else {
            newCountdowns[auction._id] = `${seconds}s`;
          }
        }
      });
      setCountdowns(newCountdowns);
      setIsComingSoon(newIsComingSoon);
    };

    // Update immediately
    updateCountdowns();

    // Update every second
    const interval = setInterval(updateCountdowns, 1000);

    return () => clearInterval(interval);
  }, [auctions]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAuctions(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Update symbol options from coin list
  useEffect(() => {
    if (coinList1 && coinList1.length > 0) {
      const SmallLabel = ({ text, smallText }) => (
        <span>
          {text}{" "}
          <small>
            <b>{smallText}</b>
          </small>
        </span>
      );

      const options = coinList1.map((item) => {
        const symbolText = item?.symbol === "META" ? "MET$" : item?.symbol;
        const chainText = item?.chain_name ? ` (${item.chain_name})` : '';
        return {
          value: item?.symbol,
          label: <SmallLabel text={`${symbolText}${chainText}`} smallText={item?.quantity} />,
          price: item?.price,
          chain_id: item?.chain_id,
          symbol: item?.symbol,
          chain_name: item?.chain_name,
        };
      });

      setSymbolOptions(options);
    }
  }, [coinList1]);

  useEffect(() => {
    if (selectedCategory && categories.length > 0) {
      const category = categories.find(cat => cat.value === selectedCategory);
      if (category?.sub_categories) {
        const subcatOptions = category.sub_categories.map(sub => ({
          value: sub._id,
          label: sub.sub_category_name,
        }));
        setSubcategories(subcatOptions);
      }
    }
  }, [selectedCategory, categories]);

  const fetchAuctions = async (page = currentPage) => {
    try {
      setIsLoading(true);
      const payload = {
        page_number: page,
        limit: itemsPerPage,
      };
      const response = await getAuctionList(payload);
      
      // Handle response format: { success: true, status: 200, data: [...], count: 2, Message: "..." }
      if (response.data?.data && Array.isArray(response.data.data)) {
        setAuctions(response.data.data);
        // Use count from response as total items in database
        if (response.data?.count !== undefined) {
          const total = response.data.count;
          setTotalItems(total);
          setTotalPages(Math.ceil(total / itemsPerPage));
        }
      } else if (response.data?.auctions) {
        setAuctions(response.data.auctions);
        if (response.data?.count !== undefined) {
          const total = response.data.count;
          setTotalItems(total);
          setTotalPages(Math.ceil(total / itemsPerPage));
        }
      } else if (response.data?.result) {
        setAuctions(response.data.result);
        if (response.data?.count !== undefined) {
          const total = response.data.count;
          setTotalItems(total);
          setTotalPages(Math.ceil(total / itemsPerPage));
        }
      } else if (Array.isArray(response.data)) {
        setAuctions(response.data);
        setTotalItems(response.data.length);
        setTotalPages(Math.ceil(response.data.length / itemsPerPage));
      }
    } catch (error) {
      console.error('Error fetching auctions:', error);
      toast.error('Failed to load auctions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewAuction = (auctionId, storeId) => {
   console.log("🚀 ~ handleViewAuction ~ auctionId:", auctionId)
   
      history.push(`/productdetails?id=${auctionId}`);

  };

  const fetchCategories = async () => {
    try {
      const response = await getCategory();
      if (response.data?.category) {
        const options = response.data.category.map(cat => ({
          value: cat._id,
          label: cat.category_name,
          sub_categories: cat.sub_categories || [],
        }));
        setCategories(options);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleOpenAuctionSidebar = () => {
    if (!isWholeseller) {
      toast.error('Only wholesellers can create auctions', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setAuctionSidebarOpen(true);
  };

  const handleCloseAuctionSidebar = () => {
    setAuctionSidebarOpen(false);
    resetAuctionForm();
  };

  const resetAuctionForm = () => {
    setAuctionTitle('');
    setAuctionBrand('');
    setAuctionDescription('');
    setAuctionStartTime('');
    setAuctionEndTime('');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setProductCondition('New');
    setPickupAddress('');
    setWarrantyMonths(0);
    setDeliveryCharges(0);
    setLogoFile(null);
    setPdfFiles([]);
    setTiers([]);
    setTierMinQuantity('');
    setTierMaxQuantity('');
    setTierPrice('');
    setFeatures([{ id: 1, value: '' }]);
    setSelectedSymbol(null);
  };

  const handleAddTier = () => {
    if (!tierMinQuantity || !tierMaxQuantity || !tierPrice) {
      toast.error('Please fill min quantity, max quantity, and price');
      return;
    }
    const minQty = parseInt(tierMinQuantity, 10);
    const maxQty = parseInt(tierMaxQuantity, 10);
    if (minQty >= maxQty) {
      toast.error('Max quantity must be greater than min quantity');
      return;
    }
    setTiers([...tiers, { minQty, maxQty, price: parseFloat(tierPrice) }]);
    setTierMinQuantity('');
    setTierMaxQuantity('');
    setTierPrice('');
  };

  const handleRemoveTier = (index) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const handleAddFeature = () => {
    const newId = features.length > 0 ? Math.max(...features.map(f => f.id)) + 1 : 1;
    setFeatures([...features, { id: newId, value: '' }]);
  };

  const handleRemoveFeature = (id) => {
    setFeatures(features.filter(f => f.id !== id));
  };

  const handleChangeFeature = (id, value) => {
    setFeatures(features.map(f => f.id === id ? { ...f, value } : f));
  };

  const handleCreateAuctionSubmit = async () => {
    // Validation
    if (!auctionTitle) {
      toast.error('Title is required');
      return;
    }
    if (!auctionBrand) {
      toast.error('Brand is required');
      return;
    }
    if (!auctionDescription) {
      toast.error('Description is required');
      return;
    }
    if (!auctionStartTime) {
      toast.error('Start time is required');
      return;
    }
    if (!auctionEndTime) {
      toast.error('End time is required');
      return;
    }
    if (!selectedCategory) {
      toast.error('Category is required');
      return;
    }
    if (!selectedSubcategory) {
      toast.error('Subcategory is required');
      return;
    }
    if (!pickupAddress) {
      toast.error('Pickup address is required');
      return;
    }
    if (tiers.length === 0) {
      toast.error('At least one tier is required');
      return;
    }
    if (!selectedSymbol) {
      toast.error('Sell Symbol is required');
      return;
    }
    if (!logoFile) {
      toast.error('Logo is required');
      return;
    }
    if (!pdfFiles || pdfFiles.length === 0) {
      toast.error('At least one PDF document is required');
      return;
    }

    try {
      setIsCreatingAuction(true);

      // Create FormData
      const formData = new FormData();

      // Add files
      formData.append('File', logoFile);
      // Append all PDF files
      pdfFiles.forEach((file) => {
        formData.append('File2', file);
      });
      // Get feature values
      const featureValues = features.map(f => f.value).filter(v => v.trim() !== '');
      const tierDetails = tiers.map(tier => ({
        minQty: tier.minQty,
        maxQty: tier.maxQty,
        price: tier.price,
      }));

      // Add all other fields
      formData.append('auction_start_time', auctionStartTime);
      formData.append('auction_end_time', auctionEndTime);
      formData.append('seller_wallet', user?.data?.user?.address || '');
      formData.append('tier_details', JSON.stringify(tierDetails));
      formData.append('subcategory_id', selectedSubcategory);
      formData.append('product_condition', productCondition);
      formData.append('category_id', selectedCategory);
      formData.append('pickupAddress', pickupAddress);
      formData.append('warrantyInMonths', parseInt(warrantyMonths, 10));
      formData.append('title', auctionTitle);
      formData.append('brand', auctionBrand);
      formData.append('deliveryCharges', parseFloat(deliveryCharges));
      formData.append('description', auctionDescription);
      formData.append('features', JSON.stringify(featureValues));
      formData.append('symbol', selectedSymbol?.value || selectedSymbol);
      if (selectedSymbol?.chain_id) {
        formData.append('chainId', selectedSymbol.chain_id);
      }

      const response = await addAuction(formData);

      if (response.data?.success || response.status === 200) {
        toast.success('Auction created successfully!', {
          position: toast.POSITION.TOP_RIGHT,
        });
        handleCloseAuctionSidebar();
        fetchAuctions(currentPage); // Refresh auctions list
      } else {
        toast.error('Failed to create auction', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error('Error creating auction:', error);
      toast.error(error.response?.data?.Message || 'Failed to create auction', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsCreatingAuction(false);
    }
  };

  return (
    <PageContainer>
      {isLoading && (
        <LoadingOverlay>
          <Ring size={65} color="#00f7d2" />
        </LoadingOverlay>
      )}

      <Header>
        <Title>Auctions</Title>
      </Header>

      {(() => {
        const currentUserId = user?.data?.user?.user_id ;
        const myAuctions = auctions.filter(auction => auction.user_id === currentUserId);
        const otherAuctions = auctions.filter(auction => auction.user_id !== currentUserId);
        const now = Date.now();
        const liveAuctions = otherAuctions.filter(auction => {
          const endTime = auction?.acution_end_time || auction?.auction_end_time;
          if (!endTime) return false;
          const endDate = new Date(endTime).getTime();
          return endDate > now; // End time is in the future
        });

        const pastAuctions = otherAuctions.filter(auction => {
          const endTime = auction?.acution_end_time || auction?.auction_end_time;
          if (!endTime) return false;
          const endDate = new Date(endTime).getTime();
          return endDate <= now; // End time is in the past or now
        });

        // Check if there are no auctions at all
        const hasNoAuctions = myAuctions.length === 0 && liveAuctions.length === 0 && pastAuctions.length === 0;

        return (
          <>
            {hasNoAuctions ? (
              <EmptyState>
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <p style={{ color: '#9ca3af', fontSize: '16px', margin: 0 }}>
                    No auction found
                  </p>
                </div>
              </EmptyState>
            ) : (
              <>
                {myAuctions.length > 0 && (
                  <>
                    <SectionTitle>My Auctions</SectionTitle>
                    <Grid>
                      {myAuctions.map((auction) => (
                        <AuctionCard
                          key={auction._id}
                          auction={auction}
                          countdown={countdowns[auction._id]}
                          isComingSoon={isComingSoon[auction._id]}
                          onClick={() => handleViewAuction(auction.product_id, auction.store_id)}
                        />
                      ))}
                    </Grid>
                  </>
                )}

                {liveAuctions.length > 0 && (
                  <>
                    <SectionTitle>Live Auctions</SectionTitle>
                    <Grid>
                      {liveAuctions.map((auction) => (
                        <AuctionCard
                          key={auction._id}
                          auction={auction}
                          countdown={countdowns[auction._id]}
                          isComingSoon={isComingSoon[auction._id]}
                          onClick={() => handleViewAuction(auction?.product_info?.[0]?._id, auction.store_id)}
                        />
                      ))}
                    </Grid>
                  </>
                )}

                {pastAuctions.length > 0 && (
                  <>
                    <SectionTitle>Past Auctions</SectionTitle>
                    <Grid>
                      {pastAuctions.map((auction) => (
                        <AuctionCard
                          key={auction._id}
                          auction={auction}
                          countdown={countdowns[auction._id]}
                          isComingSoon={isComingSoon[auction._id]}
                          onClick={() => handleViewAuction(auction?.product_info?.[0]?._id, auction.store_id)}
                        />
                      ))}
                    </Grid>
                  </>
                )}

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    showPageInfo={true}
                  />
                )}
              </>
            )}
          </>
        );
      })()}

      {/* Auction Creation Sidebar - Only for Wholesellers */}
      {isWholeseller && (
        <>
          <SidebarOverlay
            isOpen={auctionSidebarOpen}
            onClick={handleCloseAuctionSidebar}
          />
          <SidebarContainer isOpen={auctionSidebarOpen}>
        <SidebarTitle>Create Auction</SidebarTitle>
        
        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            value={auctionTitle}
            onChange={(e) => setAuctionTitle(e.target.value)}
            placeholder="Enter auction title"
          />
        </FormGroup>

        <FormGroup>
          <Label>Brand *</Label>
          <Input
            type="text"
            value={auctionBrand}
            onChange={(e) => setAuctionBrand(e.target.value)}
            placeholder="Enter brand name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Description *</Label>
          <TextArea
            value={auctionDescription}
            onChange={(e) => setAuctionDescription(e.target.value)}
            placeholder="Enter auction description"
          />
        </FormGroup>

        <FormGroup>
          <Label>Start Time *</Label>
          <Input
            type="datetime-local"
            value={auctionStartTime}
            onChange={(e) => setAuctionStartTime(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>End Time *</Label>
          <Input
            type="datetime-local"
            value={auctionEndTime}
            onChange={(e) => setAuctionEndTime(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Category *</Label>
          <SelectStyled
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </SelectStyled>
        </FormGroup>

        <FormGroup>
          <Label>Subcategory *</Label>
          <SelectStyled
            value={selectedSubcategory || ''}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
            disabled={!selectedCategory}
          >
            <option value="">Select subcategory</option>
            {subcategories.map(sub => (
              <option key={sub.value} value={sub.value}>{sub.label}</option>
            ))}
          </SelectStyled>
        </FormGroup>

        <FormGroup>
          <Label>Product Condition *</Label>
          <SelectStyled
            value={productCondition}
            onChange={(e) => setProductCondition(e.target.value)}
          >
            <option value="New">New</option>
            <option value="Used">Used</option>
            <option value="Refurbished">Refurbished</option>
          </SelectStyled>
        </FormGroup>



        <FormGroup>
          <Label>Pickup Address *</Label>
          <Input
            type="text"
            value={pickupAddress}
            onChange={(e) => setPickupAddress(e.target.value)}
            placeholder="Enter pickup address"
          />
        </FormGroup>

        <FormGroup>
          <Label>Warranty (Months)</Label>
          <Input
            type="number"
            value={warrantyMonths}
            onChange={(e) => setWarrantyMonths(e.target.value)}
            min="0"
          />
        </FormGroup>

        <FormGroup>
          <Label>Delivery Charges</Label>
          <Input
            type="number"
            value={deliveryCharges}
            onChange={(e) => setDeliveryCharges(e.target.value)}
            min="0"
            step="0.01"
          />
        </FormGroup>

        <FormGroup>
          <Label>Logo *</Label>
          <FileUploadButton>
            {logoFile ? logoFile.name : 'Choose Logo File'}
            <FileInput
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
            />
          </FileUploadButton>
        </FormGroup>

        <FormGroup>
          <Label>PDF Document *</Label>
          <FileUploadButton>
            {pdfFiles.length > 0 ? `${pdfFiles.length} file(s) selected` : 'Choose PDF File(s)'}
            <FileInput
              type="file"
              accept="application/pdf"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setPdfFiles(files);
              }}
            />
          </FileUploadButton>
          {pdfFiles.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
              {pdfFiles.map((file, index) => (
                <div key={index} style={{ marginTop: '0.25rem' }}>
                  • {file.name}
                </div>
              ))}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label>Sell Symbol *</Label>
          <SelectWrapper>
            <CustomSelect
              value={selectedSymbol?.value || selectedSymbol}
              onChange={(value) => {
                setSelectedSymbol(value);
              }}
              options={symbolOptions}
              placeholder="Select Symbol"
              customStyles={{
                control: {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  hover: {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid #5ce9ff',
                  },
                },
                singleValue: {
                  color: 'white',
                },
                input: {
                  color: 'white',
                },
              }}
            />
          </SelectWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Pricing Tiers *</Label>
          <TierInputGroup>
            <Input
              type="number"
              placeholder="Min Qty"
              value={tierMinQuantity}
              onChange={(e) => setTierMinQuantity(e.target.value)}
              min="1"
            />
            <Input
              type="number"
              placeholder="Max Qty"
              value={tierMaxQuantity}
              onChange={(e) => setTierMaxQuantity(e.target.value)}
              min="1"
            />
            <Input
              type="number"
              placeholder="Price"
              value={tierPrice}
              onChange={(e) => setTierPrice(e.target.value)}
              min="0"
              step="0.01"
            />
            <AddTierButton onClick={handleAddTier}>Add</AddTierButton>
          </TierInputGroup>
          {tiers.length > 0 && (
            <TierTable>
              <thead>
                <tr>
                  <th>Min Qty</th>
                  <th>Max Qty</th>
                  <th>Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, index) => (
                  <tr key={index}>
                    <td>{tier.minQty}</td>
                    <td>{tier.maxQty}</td>
                    <td>€{tier.price}</td>
                    <td>
                      <button onClick={() => handleRemoveTier(index)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TierTable>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Features</Label>
          {features.map(feature => (
            <FeatureInputGroup key={feature.id}>
              <Input
                type="text"
                value={feature.value}
                onChange={(e) => handleChangeFeature(feature.id, e.target.value)}
                placeholder="Enter feature"
              />
              <button onClick={() => handleRemoveFeature(feature.id)}>×</button>
            </FeatureInputGroup>
          ))}
          <AddTierButton onClick={handleAddFeature} style={{ marginTop: '0.5rem' }}>
            + Add Feature
          </AddTierButton>
        </FormGroup>

        <ButtonGroup>
          <CancelButton onClick={handleCloseAuctionSidebar}>Cancel</CancelButton>
          <SaveButton onClick={handleCreateAuctionSubmit} disabled={isCreatingAuction}>
            {isCreatingAuction ? 'Creating...' : 'Create Auction'}
          </SaveButton>
        </ButtonGroup>
          </SidebarContainer>
        </>
      )}
    </PageContainer>
  );
};

export default AuctionList;
